import React, { useState, useMemo, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { ArrowLeft, Lock, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { STUDIO, PACKAGES } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Flatten packages to selectable options
const buildOptions = () => {
  const opts = [];
  PACKAGES.forEach((pkg) => {
    pkg.rows.forEach((row) => {
      const price = Number(row.price.replace(/[^0-9.]/g, ""));
      if (Number.isFinite(price) && price > 0) {
        opts.push({
          id: `${pkg.name}-${row.location}`.toLowerCase().replace(/\s+/g, "-"),
          label: `${pkg.name} · ${row.location}`,
          duration: row.duration,
          amount: price,
        });
      }
    });
  });
  opts.push({ id: "custom", label: "Custom amount", duration: null, amount: 0 });
  return opts;
};

export default function Payments() {
  const options = useMemo(buildOptions, []);
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const preselectId = params.get("pkg");
  const bookingId = params.get("booking_id");
  const preselect = options.find((o) => o.id === preselectId) || options[2];

  const [selected, setSelected] = useState(preselect);
  const [paymentType, setPaymentType] = useState("full"); // 'full' | 'deposit'
  const [customAmount, setCustomAmount] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [clientId, setClientId] = useState("");
  const [mode, setMode] = useState("sandbox");
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [booking, setBooking] = useState(null);
  const [loadingBooking, setLoadingBooking] = useState(!!bookingId);

  useEffect(() => {
    axios
      .get(`${API}/payments/config`)
      .then((r) => {
        setClientId(r.data.client_id);
        setMode(r.data.mode);
      })
      .catch(() => toast.error("Payment system unavailable. Please contact us."))
      .finally(() => setLoadingConfig(false));
  }, []);

  // If landed from booking flow, fetch booking
  useEffect(() => {
    if (!bookingId) return;
    axios
      .get(`${API}/bookings/${bookingId}`)
      .then((r) => {
        setBooking(r.data);
        setName(r.data.customer_name || "");
        setEmail(r.data.customer_email || "");
      })
      .catch(() => toast.error("Could not load your booking. Please try again."))
      .finally(() => setLoadingBooking(false));
  }, [bookingId]);

  const baseAmount =
    selected.id === "custom"
      ? Math.max(0, parseFloat(customAmount || "0"))
      : selected.amount;

  const bookingMode = !!booking;
  const finalAmount = bookingMode
    ? booking.deposit_amount
    : paymentType === "deposit"
    ? Math.round(baseAmount * 50) / 100
    : baseAmount;
  const canPay = finalAmount > 0 && !loadingConfig && clientId && !loadingBooking;

  const purpose = bookingMode
    ? `${booking.package} · ${booking.date} ${booking.start_time} · 50% Deposit`
    : `${selected.label}${
        paymentType === "deposit" ? " · 50% Deposit" : " · Full Payment"
      }`;

  return (
    <main data-testid="payments-page" className="min-h-screen bg-[#FDFBF7] text-[#1A1A1A]">
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <Link
          to="/"
          data-testid="payments-back-link"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-[#4A4A4A] hover:text-[#C5A059] transition-colors"
        >
          <ArrowLeft size={14} /> Back to lsb-studio
        </Link>

        <p className="overline mt-10">Secure Checkout</p>
        <h1 className="font-serif-display text-4xl md:text-6xl leading-[1.05] tracking-tight mt-4">
          {bookingMode ? (
            <>
              Lock your date,
              <br />
              <span className="italic font-light">with a deposit.</span>
            </>
          ) : (
            <>
              Reserve your session,
              <br />
              <span className="italic font-light">effortlessly.</span>
            </>
          )}
        </h1>
        <p className="text-[#4A4A4A] mt-6 max-w-xl leading-relaxed">
          {bookingMode
            ? "Complete the 50% deposit below to confirm your session. Payments are processed securely by PayPal."
            : "Choose a package or enter a custom amount, then choose full payment or a 50% deposit. Payments are processed securely by PayPal."}
        </p>

        {mode !== "live" && (
          <div
            data-testid="sandbox-banner"
            className="mt-8 border border-[#C5A059] bg-[#F2EFE9] p-4 text-sm"
          >
            <strong className="font-serif-display">Sandbox mode.</strong> No
            real money will be charged.
          </div>
        )}

        <div className="mt-12 grid grid-cols-1 md:grid-cols-5 gap-10">
          {/* Left: selection */}
          <div className="md:col-span-3 space-y-10">
            {bookingMode ? (
              <section data-testid="booking-summary">
                <p className="overline mb-4">Your booking</p>
                {loadingBooking ? (
                  <p className="text-sm text-[#4A4A4A]">Loading booking…</p>
                ) : (
                  <div className="p-6 border border-[#C5A059] bg-[#F2EFE9] space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-[#4A4A4A]">Package</span>
                      <span className="font-serif-display">
                        {booking.package}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-[#4A4A4A]">Date</span>
                      <span className="font-serif-display">{booking.date}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-[#4A4A4A]">Time</span>
                      <span className="font-serif-display">
                        {booking.start_time} · {booking.duration_hours}h
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-[#4A4A4A]">Name</span>
                      <span className="font-serif-display">
                        {booking.customer_name}
                      </span>
                    </div>
                    <div className="pt-3 border-t border-black/10 flex justify-between text-sm">
                      <span className="text-[#4A4A4A]">Full session price</span>
                      <span className="font-serif-display">
                        ${booking.package_price.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="overline">Deposit due (50%)</span>
                      <span className="font-serif-display text-lg">
                        ${booking.deposit_amount.toFixed(2)}
                      </span>
                    </div>
                    <p className="text-xs text-[#4A4A4A] pt-1">
                      Balance ${booking.deposit_amount.toFixed(2)} due at session.
                    </p>
                  </div>
                )}
              </section>
            ) : (
              <>
                <section>
                  <p className="overline mb-4">1 · Choose a package</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {options.map((o) => (
                      <button
                        key={o.id}
                        data-testid={`pay-option-${o.id}`}
                        onClick={() => setSelected(o)}
                        className={`text-left p-4 border transition-colors ${
                          selected.id === o.id
                            ? "border-[#C5A059] bg-[#F2EFE9]"
                            : "border-black/10 hover:border-black/30"
                        }`}
                      >
                        <p className="font-serif-display text-lg">{o.label}</p>
                        <p className="text-xs text-[#4A4A4A] mt-1">
                          {o.duration
                            ? `${o.duration} · $${o.amount.toFixed(2)}`
                            : "Enter any amount"}
                        </p>
                      </button>
                    ))}
                  </div>
                  {selected.id === "custom" && (
                    <div className="mt-4">
                      <label className="overline">Amount (USD)</label>
                      <input
                        data-testid="pay-custom-amount"
                        type="number"
                        min="1"
                        step="0.01"
                        value={customAmount}
                        onChange={(e) => setCustomAmount(e.target.value)}
                        className="line-input"
                        placeholder="e.g. 250.00"
                      />
                    </div>
                  )}
                </section>

                <section>
                  <p className="overline mb-4">2 · Choose payment type</p>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      data-testid="pay-type-full"
                      onClick={() => setPaymentType("full")}
                      className={`p-4 border text-left ${
                        paymentType === "full"
                          ? "border-[#C5A059] bg-[#F2EFE9]"
                          : "border-black/10 hover:border-black/30"
                      }`}
                    >
                      <p className="font-serif-display text-lg">Full Payment</p>
                      <p className="text-xs text-[#4A4A4A] mt-1">Pay 100% now</p>
                    </button>
                    <button
                      data-testid="pay-type-deposit"
                      onClick={() => setPaymentType("deposit")}
                      className={`p-4 border text-left ${
                        paymentType === "deposit"
                          ? "border-[#C5A059] bg-[#F2EFE9]"
                          : "border-black/10 hover:border-black/30"
                      }`}
                    >
                      <p className="font-serif-display text-lg">50% Deposit</p>
                      <p className="text-xs text-[#4A4A4A] mt-1">Balance due day-of</p>
                    </button>
                  </div>
                </section>

                <section>
                  <p className="overline mb-4">3 · Your details (optional)</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
                    <div>
                      <label className="overline">Name</label>
                      <input
                        data-testid="pay-name"
                        className="line-input"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Full name"
                      />
                    </div>
                    <div>
                      <label className="overline">Email</label>
                      <input
                        data-testid="pay-email"
                        type="email"
                        className="line-input"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@email.com"
                      />
                    </div>
                  </div>
                </section>
              </>
            )}
          </div>

          {/* Right: summary + pay button */}
          <aside className="md:col-span-2 bg-[#F2EFE9] p-6 md:p-8 h-fit sticky top-28">
            <p className="overline">Order summary</p>
            <div className="mt-4 space-y-3 pb-4 border-b border-black/10">
              <div className="flex justify-between text-sm">
                <span className="text-[#4A4A4A]">Package</span>
                <span className="font-serif-display text-right">
                  {bookingMode ? booking?.package : selected.label}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#4A4A4A]">Payment type</span>
                <span className="font-serif-display">
                  {bookingMode
                    ? "50% Deposit"
                    : paymentType === "deposit"
                    ? "50% Deposit"
                    : "Full"}
                </span>
              </div>
            </div>
            <div className="flex items-baseline justify-between mt-5">
              <span className="overline">Total due now</span>
              <span
                data-testid="pay-total"
                className="font-serif-display text-3xl md:text-4xl"
              >
                ${finalAmount.toFixed(2)}
              </span>
            </div>

            <div className="mt-8">
              {loadingConfig ? (
                <p className="text-sm text-[#4A4A4A]">Loading PayPal…</p>
              ) : !clientId ? (
                <p className="text-sm text-red-700">
                  Payment system not configured. Please contact {STUDIO.email}.
                </p>
              ) : !canPay ? (
                <p className="text-sm text-[#4A4A4A]">
                  Enter an amount to continue.
                </p>
              ) : (
                <PayPalScriptProvider
                  options={{
                    "client-id": clientId,
                    currency: "USD",
                    intent: "capture",
                  }}
                >
                  <PayPalButtons
                    key={`${finalAmount}-${purpose}`}
                    data-testid="paypal-buttons"
                    style={{
                      layout: "vertical",
                      shape: "rect",
                      color: "gold",
                      label: "pay",
                    }}
                    createOrder={async () => {
                      try {
                        const { data } = await axios.post(
                          `${API}/payments/create-order`,
                          {
                            amount: finalAmount,
                            purpose,
                            package: bookingMode ? booking.package : selected.label,
                            payment_type: bookingMode ? "deposit" : paymentType,
                            customer_name: name || null,
                            customer_email: email || null,
                          }
                        );
                        // Link PayPal order to booking for reconciliation
                        if (bookingMode) {
                          axios
                            .post(
                              `${API}/bookings/${booking.id}/attach-payment?paypal_order_id=${data.id}`
                            )
                            .catch(() => {});
                        }
                        return data.id;
                      } catch (e) {
                        toast.error("Could not start payment. Please try again.");
                        throw e;
                      }
                    }}
                    onApprove={async (data) => {
                      try {
                        const res = await axios.post(
                          `${API}/payments/capture-order/${data.orderID}`
                        );
                        if (
                          res.data.status === "COMPLETED" ||
                          res.data.status === "APPROVED"
                        ) {
                          if (bookingMode) {
                            await axios.post(
                              `${API}/bookings/${booking.id}/confirm`
                            );
                            toast.success(
                              "Session confirmed! We'll follow up at your email."
                            );
                            setTimeout(() => navigate("/"), 2500);
                          } else {
                            toast.success(
                              "Payment received. Thank you — we'll be in touch shortly."
                            );
                          }
                        } else {
                          toast.info(`Payment status: ${res.data.status}`);
                        }
                      } catch (e) {
                        toast.error(
                          "Payment captured by PayPal but we couldn't confirm. We'll reach out."
                        );
                      }
                    }}
                    onError={() =>
                      toast.error("PayPal error. Please try again or contact us.")
                    }
                    onCancel={() => toast.info("Payment cancelled.")}
                  />
                </PayPalScriptProvider>
              )}
            </div>

            <div className="mt-6 flex items-start gap-2 text-xs text-[#4A4A4A]">
              <ShieldCheck className="w-4 h-4 text-[#C5A059] flex-shrink-0 mt-0.5" />
              <p>
                Payments are handled securely by PayPal. Your card details never
                touch lsb-studio's servers.
              </p>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}
